import { useAuth } from "app/context/auth-context";
import { UrlBuilder } from "app/helpers/MiscUtils";
import { AdminRoutes } from "app/modules/admin/AdminPage";
import { JobfairService } from "app/services/JobfairService";
import { JobFair, Roles } from "app/types";
import {
    Pencil,
    DoorOpen,
    Calendar3,
    Clock,
    HourglassSplit,
    Clipboard,
    Trash
} from "react-bootstrap-icons";
import { Button } from "react-bootstrap-v5";
import { useHistory } from "react-router-dom";
import { config } from "../../../../config";

interface JobfairCardProps {
    fair: JobFair;
    onActivationToggle?: (fair: JobFair) => void;
    onEditJobFair?: (fair: JobFair) => void;
    onDeleteJobFair?: (fair: JobFair) => void;
}

export const JobfairCard = (props: JobfairCardProps) => {
    const history = useHistory();
    const jobfairService = new JobfairService();
    const { user } = useAuth();
    const handleActivation = async (fair: JobFair) => {
        const updatedFair = {
            ...fair,
            isActivated: !fair.isActivated
        };

        await jobfairService.updateJobFair(updatedFair).then(() => {
            if (props.onActivationToggle)
                props.onActivationToggle(updatedFair);
        })
    };

    return (<div className='col-md-6 col-xl-4 mt-8'>
        <div className='card h-100 shadow-sm hover-elevate-up transition-300'>
            <div className='card-header border-0 pt-9 ribbon ribbon-start ribbon-clip'>
                {props.fair.paymentStatus == 'Paid' && <div className={`ribbon-label`}>
                    Paid
                    <span className="ribbon-inner bg-success"></span>
                </div>}

                <div className='card-title m-0'>
                    <span className='fs-3 fw-bolder text-dark'>{props.fair.name}</span>
                </div>

                {user?.roles?.includes(Roles.Admin) &&
                    <div className='card-toolbar'>
                        <Button
                            variant='light-primary'
                            size="sm"
                            className="btn-icon me-2"
                            onClick={() => {
                                history.push(UrlBuilder(AdminRoutes.Lobby, { orgId: props.fair.companyId, jobFairId: props.fair.id }));
                            }}
                            title="Enter Lobby"
                        >
                            <DoorOpen size={18} />
                        </Button>

                        {props.onEditJobFair && (
                            <Button
                                variant='light-info'
                                size="sm"
                                className="btn-icon me-2"
                                onClick={() => props.onEditJobFair?.(props.fair)}
                                title="Edit Job Fair"
                            >
                                <Pencil size={18} />
                            </Button>
                        )}

                        {!props.fair.isActivated && props.onDeleteJobFair && (
                            <Button
                                variant='light-danger'
                                size="sm"
                                className="btn-icon"
                                onClick={() => props.onDeleteJobFair?.(props.fair)}
                                title="Delete Job Fair"
                            >
                                <Trash size={18} />
                            </Button>
                        )}
                    </div>}
            </div>

            <div className='card-body p-9'>
                <div className='d-flex align-items-center mb-6'>
                    <HourglassSplit className='text-primary me-2' size={20} />
                    <span className="fs-6 fw-bold text-gray-700">
                        Duration: {props.fair.durationHours}h {props.fair.durationMinutes}m
                    </span>
                </div>

                <div className='row g-0 mb-6'>
                    <div className='col-6 bg-light rounded-start p-4 border-end border-white'>
                        <div className='d-flex align-items-center'>
                            <Calendar3 className='text-primary fs-2 me-3' size={24} />
                            <div>
                                <div className='fs-8 fw-bold text-gray-500'>Start Date</div>
                                <div className='fs-7 fw-bolder text-gray-800'>{props.fair.startDate}</div>
                            </div>
                        </div>
                    </div>
                    <div className='col-6 bg-light rounded-end p-4'>
                        <div className='d-flex align-items-center'>
                            <Clock className='text-primary fs-2 me-3' size={24} />
                            <div>
                                <div className='fs-8 fw-bold text-gray-500'>Start Time</div>
                                <div className='fs-7 fw-bolder text-gray-800'>{props.fair.startTime}</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className='mb-6'>
                    {props.fair.isActivated &&
                        <div className="w-100">
                            <label className='form-label fw-bold text-gray-700 fs-7'>Job Seeker Link</label>
                            <div className='input-group input-group-solid'>
                                <input
                                    title={`${config.vjfpBaseUrl}${props.fair.alias ? `join/${props.fair.alias}` : `job-seeker/organizations/${props.fair.companyId}/jobfairs/${props.fair.id}`}`}
                                    type='text'
                                    className='form-control form-control-sm form-control-solid'
                                    value={`${config.vjfpBaseUrl}job-seeker/organizations/${props.fair.companyId}/jobfairs/${props.fair.id}`}
                                    readOnly
                                />
                                <span
                                    className='input-group-text cursor-pointer hover-primary'
                                    onClick={() => {
                                        navigator.clipboard.writeText(`${config.vjfpBaseUrl}${props.fair.alias ? `join/${props.fair.alias}` : `job-seeker/organizations/${props.fair.companyId}/jobfairs/${props.fair.id}`}`);
                                        const label = document.getElementById(`copy-label-${props.fair.id}`);
                                        label!.classList.remove('d-none');
                                        setTimeout(() => label!.classList.add('d-none'), 2000);
                                    }}
                                >
                                    <Clipboard size={18} />
                                </span>
                            </div>
                            <div className="d-flex justify-content-end mt-1" style={{ minHeight: '20px' }}>
                                <span id={`copy-label-${props.fair.id}`} className='text-success fw-bold fs-8 d-none'>Copied!</span>
                            </div>
                        </div>
                    }
                </div>

                {user?.roles?.includes(Roles.Admin) &&
                    <div className={`notice d-flex bg-light-${props.fair.isActivated ? 'success' : 'danger'} rounded border-${props.fair.isActivated ? 'success' : 'danger'} border border-dashed p-4 flex-column`}>
                        <div className='d-flex flex-stack'>
                            <div className="fw-bold">
                                <div className={`fs-7 text-gray-700`}>Status</div>
                                <div className={`fs-6 fw-bolder text-${props.fair.isActivated ? 'success' : 'danger'}`}>
                                    {props.fair.isActivated ? 'Active' : 'Inactive'}
                                </div>
                            </div>

                            <Button
                                id={`toggle-${props.fair.id}`}
                                variant={props.fair.isActivated ? 'danger' : 'success'}
                                size="sm"
                                className="px-4"
                                onClick={() => handleActivation(props.fair)}
                            >
                                {props.fair.isActivated ? 'Deactivate' : 'Activate'}
                            </Button>
                        </div>
                    </div>}
            </div>
        </div>
    </div >);
}
